import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';

import { storeRehydrateAction } from '@ngrx-addons/persist-state';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  filter,
  map,
  mergeMap,
  retry,
  switchMap,
  tap,
} from 'rxjs/operators';

import { AppContextService } from '@/app/services/app-context.service';
import { ConfigurationService } from '@/app/services/configuration.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { concatLatestFrom } from '@ngrx/operators';
import {
  routerNavigationAction,
  routerRequestAction,
} from '@ngrx/router-store';
import {
  selectCurrentClient,
  selectSelectableClients,
} from '@selectors/client.selector';
import { ClientActions } from '../actions/client.actions';
import { ConfigurationActions } from '../actions/configuration.actions';
import { ContextActions } from '../actions/context.actions';
import { LoggedInUserActions } from '../actions/logged-in-user.actions';
import { PageHeaderActions } from '../actions/page-header.actions';
import { StatusActions } from '../actions/status.actions';
import { clientFeatureKey } from '../reducers/client.reducer';
import { loggedInUserFeatureKey } from '../reducers/logged-in-user.reducer';
import {
  selectIsContextLoading,
  selectIsUserContextLoaded,
} from '../selectors/composite.selector';
import { selectIsConfigurationLoading } from '../selectors/configuration.selectors';
import {
  selectErrorInfo,
  selectIsIdPError,
} from '../selectors/error.selectors';
import { selectShowClientSelector } from '../selectors/page-header.selectors';
import { selectIsEditing } from '../selectors/status.selector';
import { LoggedInUserState } from '../state';

@Injectable()
export class AppEffects {
  loadContext$;
  loadingContext$;
  loadContextFailure$;
  loadContext401Error$;
  loadContextIdPFailure$;
  // Effects
  clientStateRehydrated$;
  userStateRehydrated$;
  clearUserIdleTimeout$;
  clientSelectedForConfig$;

  configurationLoadSuccess$;

  stopEditingOnRouteChanged$;
  showClientSelectorOnRouteChanged$;

  checkAndLoadAppContext$;

  constructor(
    router: Router,
    actions$: Actions,
    store: Store,
    auth: AuthService,
    appContextService: AppContextService,
    configurationService: ConfigurationService,
  ) {
    this.checkAndLoadAppContext$ = createEffect(() => {
      return combineLatest([
        actions$.pipe(ofType(routerRequestAction)),
        auth.isAuthenticated$.pipe(filter((isAuth) => isAuth === true)),
      ]).pipe(
        concatLatestFrom(() => [store.select(selectIsUserContextLoaded)]),
        filter(
          ([[action, isAuthenticated], contextLoaded]) =>
            action.payload.event.url !== '/' &&
            action.payload.event.url !== '/error' &&
            action.payload.event.url !== '/page-not-found' &&
            isAuthenticated &&
            !contextLoaded,
        ),
        map(() => ContextActions.load()),
      );
    });

    this.loadingContext$ = createEffect(() => {
      return actions$.pipe(
        ofType(ContextActions.load),
        concatLatestFrom(() => [store.select(selectIsContextLoading)]),
        filter(([, isContextLoading]) => !isContextLoading),
        map(() => ContextActions.loading()),
      );
    });
    this.loadContext$ = createEffect(() => {
      return actions$.pipe(
        ofType(ContextActions.loading),
        switchMap(() => auth.user$),
        switchMap((currentUser) => {
          return appContextService
            .loadAppContext(currentUser?.sub ?? null)
            .pipe(
              map((data) => ({ data, error: null })),
              catchError((error) => of({ data: null, error })),
            );
        }),
        map(({ data, error }) =>
          error !== null
            ? ContextActions.loadFailure({
                error: error,
              })
            : ContextActions.loadSuccess({ initialAppContext: data! }),
        ),
      );
    });

    this.loadContext401Error$ = createEffect(() => {
      return actions$.pipe(
        ofType(ContextActions.loadFailure),
        concatLatestFrom(() => [store.select(selectIsIdPError)]),
        filter(
          ([{ error }, isIdPError]) =>
            !isIdPError &&
            error instanceof HttpErrorResponse &&
            error.status === 401,
        ),
        tap((error) => {
          console.error(
            'Context load failed with 401 error, likely due to an expired token. Logging out.',
            error,
          );
        }),
        map(() => LoggedInUserActions.logout({})),
      );
    });
    this.loadContextIdPFailure$ = createEffect(
      () => {
        return actions$.pipe(
          ofType(ContextActions.loadFailure),
          concatLatestFrom(() => [
            store.select(selectIsIdPError),
            store.select(selectErrorInfo),
          ]),
          filter(([, isIdPError]) => isIdPError),
          tap(() => {
            router.navigate(['/error']);
          }),
        );
      },
      { dispatch: false },
    );
    this.loadContextFailure$ = createEffect(
      () => {
        return actions$.pipe(
          ofType(ContextActions.loadFailure),
          concatLatestFrom(() => [store.select(selectIsIdPError)]),
          filter(
            ([{ error }, isIdPError]) =>
              !isIdPError ||
              !(error instanceof HttpErrorResponse) ||
              error.status !== 401,
          ),
          tap(() => {
            router.navigate(['/page-not-found']);
          }),
        );
      },
      { dispatch: false },
    );

    this.stopEditingOnRouteChanged$ = createEffect(() => {
      return actions$.pipe(
        ofType(routerNavigationAction),
        concatLatestFrom(() => store.select(selectIsEditing)),
        filter(([_action, editing]) => editing),
        map(() => StatusActions.setEditing({ editing: false })),
      );
    });
    this.showClientSelectorOnRouteChanged$ = createEffect(() => {
      return actions$.pipe(
        ofType(routerNavigationAction),
        concatLatestFrom(() => store.select(selectShowClientSelector)),
        filter(([_action, show]) => !show),
        map(() => PageHeaderActions.showClientSelector({ show: true })),
      );
    });

    this.clientStateRehydrated$ = createEffect(() => {
      return actions$.pipe(
        ofType(storeRehydrateAction),
        filter((action) => !!action.features[clientFeatureKey]),
        map((action) => {
          return {
            clients: selectSelectableClients(action.features),
            selectedClient: selectCurrentClient(action.features),
          };
        }),
        map((selectedRehydratedState) => {
          if (selectedRehydratedState.selectedClient) {
            return ClientActions.selectClient({
              client: selectedRehydratedState.selectedClient,
            });
          } else if (selectedRehydratedState.clients.length === 1) {
            return ClientActions.selectClient({
              client: selectedRehydratedState.clients[0],
            });
          } else {
            return ClientActions.clearClientSelection();
          }
        }),
      );
    });

    const userStateRehydrated$ = actions$.pipe(
      ofType(storeRehydrateAction),
      filter((action) => !!action.features[loggedInUserFeatureKey]),
    );

    this.userStateRehydrated$ = createEffect(() => {
      return userStateRehydrated$.pipe(
        map((action) => {
          const loggedInUserState = action.features[
            loggedInUserFeatureKey
          ] as LoggedInUserState;
          return LoggedInUserActions.setUsers({
            ftUser: loggedInUserState.ftUser!,
            user: loggedInUserState.user!,
          });
        }),
      );
    });

    this.clearUserIdleTimeout$ = createEffect(() => {
      return userStateRehydrated$.pipe(
        map((_action) => {
          return LoggedInUserActions.clearIdleTimeout();
        }),
      );
    });

    this.clientSelectedForConfig$ = createEffect(() => {
      return actions$.pipe(
        ofType(ClientActions.selectClient),
        concatLatestFrom(() => [store.select(selectIsConfigurationLoading)]),
        filter(([action, isLoading]) => {
          // If the configuration is already loading, do not trigger another load.
          if (isLoading) {
            return false;
          }
          // This seems silly, but action.client could be an object or number.
          // This test is checking that either one of those is greater than 0.
          if (action.client === null) {
            return false;
          } else if (typeof action.client === 'object') {
            return action.client.id > 0;
          } else {
            return action.client > 0;
          }
        }),
        debounceTime(100), // This is really only applicable when a page refreshes such as when doing local dev work.
        map(([action]) => {
          return ConfigurationActions.loadConfiguration({
            client: action.client,
          });
        }),
      );
    });

    this.configurationLoadSuccess$ = createEffect(() => {
      return actions$.pipe(
        ofType(ConfigurationActions.loadConfiguration),
        mergeMap((configAction) => {
          if (typeof configAction.client === 'object') {
            return configurationService.loadConfiguration(
              configAction.client.id,
            );
          } else {
            return configurationService.loadConfiguration(configAction.client);
          }
        }),
        map((config) => {
          return ConfigurationActions.loadConfigurationSuccess({
            data: {
              ...config,
              ftconnect: {
                clientId: config.paymentCenter?.clientId ?? 0,
                customerKey: config.paymentCenter?.customerKey ?? 'undefined',
                idPId: config.paymentCenter?.idPId ?? 'undefined',
              },
            },
          });
        }),
        retry({
          count: 2,
          delay: 1000,
        }),
        catchError((err) => {
          let clientId: string | number | undefined = undefined;
          let upstreamError: HttpErrorResponse | undefined = undefined;
          if (
            typeof err === 'object' &&
            err.clientId !== undefined &&
            err.upstreamError !== undefined
          ) {
            if (
              typeof err.clientId === 'number' ||
              typeof err.clientId === 'string'
            ) {
              clientId = err.clientId;
            }
            if (err.upstreamError instanceof HttpErrorResponse) {
              upstreamError = err.upstreamError;
            }
          }

          return of(
            ConfigurationActions.loadConfigurationFailure({
              failureInformation:
                clientId !== undefined || upstreamError !== undefined
                  ? {
                      clientId,
                      httpResponseError: err.upstreamError,
                    }
                  : undefined,
            }),
          );
        }),
      );
    });
  }
}
